/**
 * /venues/:venueSlug/admin — the venue console's front page.
 *
 * Answers one question on arrival: is anything happening tonight? Everything
 * else on the page is secondary to the live tournament block, which is why that
 * block is the only thing here carrying a tinted background.
 *
 * FOUR CALLS, IN PARALLEL, AFTER the venue id is resolved. Every one of them is
 * an enhancement: each section is absent when its call fails rather than taking
 * the page down with it. A dashboard that 500s because the standings query is
 * slow is worse than a dashboard missing its standings.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { formatDateShort, formatDateTime, formatAvg } from '../lib/format.js';

/**
 * The soonest tournament still open, which is NOT the list's first row — the
 * API orders date DESC for the admin lists that page through history, so row
 * zero is the furthest away.
 *
 * Duplicated from VenueLanding.jsx rather than shared: four lines, two callers,
 * and no lib file exists yet that it would naturally belong to.
 */
function nextOpen(tournaments) {
  return [...tournaments]
    .filter((t) => t.date)
    .sort((a, b) => a.date.localeCompare(b.date))[0]
    ?? tournaments[0]
    ?? null;
}

function Tile({ label, value, meta }) {
  return (
    <div className="hd-tile">
      <div className="hd-tile-label">{label}</div>
      <div className="hd-tile-value">{value}</div>
      {meta && <div className="hd-tile-meta">{meta}</div>}
    </div>
  );
}

export default function AdminDashboard() {
  const { venueSlug } = useParams();
  const contextVenue = useVenue(venueSlug);

  // Platform staff hold no user_venue_roles row, so the auth context has no
  // entry for a venue they can nonetheless administer (see AppShell's note on
  // canAdmin). That is what the by-slug fallback below is for.
  const contextId = contextVenue?.venue_id ?? null;

  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });

    (async () => {
      let venueId = contextId;
      let venueName = contextVenue?.name ?? null;

      if (venueId === null) {
        const { data, error } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
        if (cancelled) return;

        if (error || !data?.venue) {
          setState({ status: 'error', error: error ?? 'Venue not found' });
          return;
        }

        venueId   = data.venue.id;
        venueName = data.venue.name;
      }

      const [live, open, standings, players] = await Promise.all([
        apiGet(`/api/venues/${venueId}/tournaments?status=active`),
        apiGet(`/api/venues/${venueId}/tournaments?status=registration`),
        apiGet(`/api/venues/${venueId}/public-standings?limit=5`),
        apiGet(`/api/venues/${venueId}/players`),
      ]);

      if (cancelled) return;

      setState({
        status:    'ready',
        venueId,
        venueName,
        // Date DESC from the API; the most recent active night is the one
        // being run. More than one at once is possible but not normal.
        live:      (live.data?.tournaments ?? [])[0] ?? null,
        open:      nextOpen(open.data?.tournaments ?? []),
        standings: standings.data ?? null,
        // playerCount from /players is venue membership. The standings figure
        // counts entrants in the ACTIVE SEASON, which is a different number and
        // reads 0 between seasons — it is the fallback, not the source.
        playerCount: players.data?.playerCount ?? standings.data?.playerCount ?? 0,
      });
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, contextVenue?.name]);

  if (state.status === 'loading') {
    return <div className="hd-empty">Loading…</div>;
  }

  if (state.status === 'error') {
    return (
      <div className="hd-stack">
        <h1 className="hd-page-title">Dashboard</h1>
        <p className="hd-muted">{state.error}</p>
      </div>
    );
  }

  const { venueName, live, open, standings, playerCount } = state;

  const base    = `/venues/${venueSlug}`;
  const season  = standings?.season ?? null;
  const table   = standings?.standings ?? [];

  // "Nothing here yet" is a real state for a venue that was approved an hour
  // ago, and it should read as a welcome rather than as four empty sections.
  const isNew = !season && !live && !open && table.length === 0;

  return (
    <div className="hd-stack">
      <div>
        <h1 className="hd-page-title">Dashboard</h1>
        <p className="hd-meta">{venueName ?? venueSlug}</p>
      </div>

      {isNew ? (
        <section className="hd-card hd-stack">
          <h2 className="hd-section-title">Welcome to {venueName ?? 'your venue'}</h2>
          <p className="hd-muted">
            Nothing is running yet. A season holds your tournaments together and
            is what the standings table is built from — that is the place to
            start.
          </p>
          <div className="hd-row">
            <Link className="hd-btn hd-btn-primary" to={`${base}/admin/seasons`}>
              Create a season
            </Link>
          </div>
        </section>
      ) : (
        <>
          {/* ---- Tiles ---------------------------------------------------- */}
          <div className="hd-tiles">
            <Tile
              label="Active season"
              value={season?.name ?? 'No active season'}
              meta={season ? formatDateShort(season.start_date) : null}
            />

            <Tile
              label="Players"
              value={playerCount}
              meta={playerCount === 1 ? 'registered player' : 'registered players'}
            />

            <Tile
              label="Next tournament"
              value={open ? (formatDateShort(open.date) ?? 'Date TBC') : 'None scheduled'}
              meta={open ? 'Registration open' : null}
            />

            {/* Only 'Live now', not the phase. tournaments.status has four
                values and none of them distinguishes groups from knockout;
                matches.stage does, and no endpoint exposes it yet. */}
            <Tile
              label="Tournament"
              value={live ? 'Live now' : 'No live tournament'}
              meta={live
                ? `${live.entry_count ?? 0} playing${live.night_number != null ? ` · night ${live.night_number}` : ''}`
                : null}
            />
          </div>

          {/* ---- Live ------------------------------------------------------ */}
          {live && (
            <section className="hd-live hd-stack">
              <div className="hd-row">
                <h2 className="hd-section-title" style={{ margin: 0 }}>Live tournament</h2>
                <span className="hd-badge hd-badge-gold">In progress</span>
              </div>

              <div>
                <div className="hd-venue-next-date">
                  {formatDateTime(live.date, live.start_time) ?? 'Tonight'}
                </div>
                <div className="hd-venue-next-meta">
                  {live.night_number != null && <>Night {live.night_number} · </>}
                  {live.season_name && <>{live.season_name} · </>}
                  {live.entry_count ?? 0} of {live.max_entries} playing
                </div>
              </div>

              <div className="hd-row">
                <Link className="hd-btn hd-btn-primary" to={`${base}/admin/tournaments`}>
                  Open admin panel
                </Link>
                <Link className="hd-btn hd-btn-secondary" to={`${base}/tournaments/${live.id}/live`}>
                  Watch live
                </Link>
              </div>
            </section>
          )}

          {/* ---- Registration open ----------------------------------------- */}
          {open && (
            <section>
              <h2 className="hd-section-title">Registration open</h2>

              <div className="hd-venue-next">
                <div className="hd-venue-next-date">
                  {formatDateTime(open.date, open.start_time) ?? 'Date to be confirmed'}
                </div>
                <div className="hd-venue-next-meta">
                  {open.night_number != null && <>Night {open.night_number} · </>}
                  {open.entry_count ?? 0} of {open.max_entries} spots taken ·{' '}
                  {Math.max(0, (open.max_entries ?? 0) - (open.entry_count ?? 0))} remaining
                </div>

                <div className="hd-row" style={{ marginTop: 16 }}>
                  <Link className="hd-btn hd-btn-secondary" to={`${base}/admin/tournaments`}>
                    Manage entries
                  </Link>
                </div>
              </div>
            </section>
          )}

          {/* ---- Standings ------------------------------------------------- */}
          {table.length > 0 && (
            <section>
              <h2 className="hd-section-title">Standings</h2>
              <p className="hd-meta" style={{ marginTop: -8, marginBottom: 12 }}>
                {season?.name}
                {standings.playerCount > table.length && (
                  <> · top {table.length} of {standings.playerCount}</>
                )}
              </p>

              <table className="hd-table">
                <thead>
                  <tr>
                    <th style={{ width: 36 }}>#</th>
                    <th>Player</th>
                    <th className="hd-table-num">Pts</th>
                    <th className="hd-table-num">Avg</th>
                  </tr>
                </thead>
                <tbody>
                  {table.map((row) => (
                    <tr key={`${row.position}-${row.displayName}`}>
                      <td className="hd-muted">{row.position}</td>
                      <td>{row.displayName ?? 'Unknown player'}</td>
                      <td className="hd-table-num">{row.totalPoints}</td>
                      <td className="hd-table-num">{formatAvg(row.seasonAvg)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <p style={{ marginTop: 12 }}>
                <Link to={`${base}/admin/seasons`}>View full standings</Link>
              </p>
            </section>
          )}

          {/* ---- Quick actions --------------------------------------------- */}
          <section>
            <h2 className="hd-section-title">Quick actions</h2>
            <div className="hd-row">
              <Link className="hd-btn hd-btn-secondary" to={`${base}/admin/tournaments`}>
                Create tournament
              </Link>
              <Link className="hd-btn hd-btn-secondary" to={`${base}/admin/players`}>
                Manage players
              </Link>
              <Link className="hd-btn hd-btn-secondary" to={`${base}/admin/seasons`}>
                Season settings
              </Link>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
